// Shared workspace header: step name, help popover, the always-on why-line,
// and (when the step is done) a quiet bar pointing at the next step. Content
// comes from the journey-steps registry so the rail and workspaces never
// disagree about what a step is called or what it's for.
import { HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { JOURNEY_STEPS, nextNotDoneStep } from '@/features/project/journey-steps';
import { StepLinkBar } from '@/features/project/StepLinkBar';
import { useStepStatuses, type StepId } from '@/features/project/step-status';

export function StepHeader({
  projectId,
  stepId,
  actions,
}: {
  projectId: string;
  stepId: StepId;
  actions?: React.ReactNode;
}) {
  const step = JOURNEY_STEPS[stepId];
  const statuses = useStepStatuses(projectId);
  const next = statuses ? nextNotDoneStep(stepId, statuses) : null;
  const showNext = statuses?.[stepId] === 'done' && next !== null;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <h1 className="text-2xl font-bold">{step.title}</h1>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground"
                  aria-label={`About ${step.title}`}
                >
                  <HelpCircle className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 text-sm">
                <p className="font-medium mb-1">{step.title}</p>
                <p className="text-muted-foreground">{step.help}</p>
              </PopoverContent>
            </Popover>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">{step.description}</p>
        </div>
        {actions}
      </div>
      {showNext && next && <StepLinkBar lead={`Next: ${next.title}.`} body={next.description} target={next} />}
    </div>
  );
}
